<template>
  <!-- Currency, tax and language (Workspace369 lib/view/workspace_details/): the currency picker
       (widgets/workspace_currency_picker.dart, the ISO catalogue searched by name, code or
       symbol), Settings › General's Regional rows (general/general_regional_section.dart), the tax
       code dialog (widgets/tax_code_dialog.dart: components summed to the total rate, Compound Tax)
       and an invoice that keeps the workspace currency it was created in (invoice currency_data).
       Strings are the app's (app_en.arb chooseCurrency, searchByCurrencyOrCode, settingsGeneral*,
       issueDate, dueDate, paid, balanceDue, editTaxCode, taxCodeName, taxComponent, componentName, rate, addComponent, compoundTax,
       invoicePageLanguage*); the business, client and amounts are sample data. States:
         picker    Choose Currency, searched for "dollar"
         general   Regional: currency and document language
         invoice   a new invoice in CAD with its tax code, dates and balance due
         taxcode   Edit Tax Code: GST 5% + PST 7%
         language  the invoice page language -->
  <StoryAppWindow :title="title" :icon="icons[iconKey]">
    <!-- Choose Currency -->
    <section class="saw-layer" :class="{ 'is-on': state === 'picker' }">
      <div class="saw-card cys-sheet">
        <b class="cys-h">Choose Currency</b>
        <div class="cys-search"><svg viewBox="0 0 24 24"><path :d="icons.search" /></svg><span>dollar</span></div>
        <ul class="cys-list cys-more">
          <li v-for="(c, i) in found" :key="c.code" :class="{ 'is-on': i === 1 }" :style="{ '--i': i }">
            <span class="cys-sym">{{ c.symbol }}</span>
            <span class="cys-name"><b>{{ c.name }}</b><small>{{ c.code }}</small></span>
            <svg v-if="i === 1" viewBox="0 0 24 24"><path :d="icons.check" /></svg>
          </li>
        </ul>
      </div>
    </section>

    <!-- Settings › General › Regional -->
    <section class="saw-layer" :class="{ 'is-on': state === 'general' }">
      <small class="cys-sub">Regional</small>
      <div class="saw-card cys-rows">
        <div v-for="r in regional" :key="r.label" class="cys-row" :class="{ 'is-new': r.isNew }"><span>{{ r.label }}</span><b>{{ r.value }}</b></div>
      </div>
      <small class="cys-sub">Tax codes</small>
      <div class="saw-card cys-rows">
        <div class="cys-row"><span>GST + PST</span><b>12%</b></div>
        <div class="cys-row"><span>GST</span><b>5%</b></div>
      </div>
    </section>

    <!-- A new invoice in the workspace currency -->
    <section class="saw-layer" :class="{ 'is-on': state === 'invoice' }">
      <div class="saw-card cys-inv">
        <div class="cys-inv-head"><span><b>INV-1042</b><small>Atelier Boréal</small></span><em class="saw-tag saw-tag--muted">CAD</em></div>
        <div class="cys-dates"><span class="cys-field"><small>Issue Date</small><span>Oct 21, 2026</span></span><span class="cys-field"><small>Due date</small><span>Nov 4, 2026</span></span></div>
        <div v-for="l in lines" :key="l.item" class="cys-line"><span>{{ l.item }}</span><span class="saw-muted">{{ l.qty }}</span><span class="saw-num">{{ l.amount }}</span></div>
        <div class="cys-tot"><span>Subtotal</span><b class="saw-num">$3,000.00</b></div>
        <div class="cys-tot cys-tax"><span>GST + PST · 12%</span><b class="saw-num">$360.00</b></div>
        <div class="cys-tot cys-total"><span>Total</span><b class="saw-num">$3,360.00</b></div>
        <div class="cys-tot cys-tax"><span>Paid</span><b class="saw-num">$0.00</b></div>
        <div class="cys-tot cys-due"><span>Balance Due</span><b class="saw-num">$3,360.00 CAD</b></div>
      </div>
    </section>

    <!-- Edit Tax Code -->
    <section class="saw-layer" :class="{ 'is-on': state === 'taxcode' }">
      <div class="saw-card cys-sheet">
        <b class="cys-h">Edit Tax Code</b>
        <div class="cys-field"><small>Tax Code Name</small><span>GST + PST</span></div>
        <small class="cys-sub">Tax Component</small>
        <div v-for="(c, i) in components" :key="c.name" class="cys-comp" :style="{ '--i': i }">
          <span class="cys-field"><small>Component Name</small><span>{{ c.name }}</span></span>
          <span class="cys-field cys-rate"><small>Rate</small><span>{{ c.rate }}</span></span>
        </div>
        <span class="saw-btn-quiet cys-add">Add Component</span>
        <div class="cys-row cys-flat"><span>Compound Tax</span><i class="cys-switch" /></div>
        <small class="saw-muted cys-hint">Components are summed to give the total rate.</small>
      </div>
    </section>

    <!-- Invoice page language -->
    <section class="saw-layer" :class="{ 'is-on': state === 'language' }">
      <div class="saw-card cys-sheet">
        <b class="cys-h">Page language</b>
        <small class="saw-muted cys-hint">The language of the labels on your invoice page, like “Amount due”. Your own text is never translated.</small>
        <ul class="cys-list cys-radios">
          <li><i class="cys-radio" /><span class="cys-name"><b>English</b></span></li>
          <li class="is-on"><i class="cys-radio is-on" /><span class="cys-name"><b>Document language (Français)</b><small>Set in your business profile. Pages stay in English until you choose one.</small></span></li>
        </ul>
      </div>
    </section>
  </StoryAppWindow>
</template>

<script setup>
const props = defineProps({
  state: { type: String, default: "invoice" },
});

const titles = { picker: "General", general: "General", invoice: "Invoice", taxcode: "General", language: "Document themes" };
const title = computed(() => titles[props.state] || "General");
const iconKey = computed(() => (props.state === "invoice" ? "receipt" : props.state === "language" ? "palette" : "tune"));

const found = [
  { symbol: "$", name: "Australian Dollar", code: "AUD" },
  { symbol: "$", name: "Canadian Dollar", code: "CAD" },
  { symbol: "$", name: "Hong Kong Dollar", code: "HKD" },
  { symbol: "$", name: "New Zealand Dollar", code: "NZD" },
];
const regional = [
  { label: "Currency", value: "CAD · Canadian Dollar", isNew: true },
  { label: "Document language", value: "Français" },
];
const lines = [
  { item: "Brand identity design", qty: "1", amount: "$2,400.00" },
  { item: "Logo revisions", qty: "2", amount: "$600.00" },
];
const components = [
  { name: "GST", rate: "5%" },
  { name: "PST", rate: "7%" },
];

const icons = {
  tune: "M3 17v2h6v-2H3zM3 5v2h10V5H3zm10 16v-2h8v-2h-8v-2h-2v6h2zM7 9v2H3v2h4v2h2V9H7zm14 4v-2H11v2h10zm-6-4h2V7h4V5h-4V3h-2v6z",
  receipt: "M18 17H6v-2h12v2zm0-4H6v-2h12v2zm0-4H6V7h12v2zM3 22l1.5-1.5L6 22l1.5-1.5L9 22l1.5-1.5L12 22l1.5-1.5L15 22l1.5-1.5L18 22l1.5-1.5L21 22V2l-1.5 1.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2 4.5 3.5 3 2v20z",
  palette: "M12 3a9 9 0 0 0 0 18c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8z",
  search: "M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z",
  check: "M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z",
};
</script>

<style scoped>
.cys-sheet { display: grid; align-content: start; gap: 8px; padding: 12px; }
.cys-h { font-size: 13.5px; font-weight: 600; }
.cys-sub { color: var(--saw-muted); font-size: 11px; font-weight: 600; }
.cys-hint { font-size: 11px; line-height: 1.4; }
.cys-search { display: flex; align-items: center; gap: 6px; border: 1px solid var(--saw-line); border-radius: 6px; background: var(--saw-wash); padding: 6px 8px; font-size: 12px; }
.cys-search svg { color: var(--saw-muted); }
.cys-list { display: grid; gap: 2px; margin: 0; padding: 0; list-style: none; }
.cys-list li { display: flex; align-items: center; gap: 10px; min-height: 42px; border-radius: 6px; padding: 4px 8px; }
.cys-list li.is-on { background: var(--saw-wash); }
.saw-layer.is-on .cys-list li { animation: cys-in 0.4s cubic-bezier(0.33, 1, 0.68, 1) calc(0.15s + var(--i, 0) * 0.08s) both; }
@keyframes cys-in { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: none; } }
.cys-sym { display: grid; flex: none; place-items: center; width: 28px; height: 28px; border-radius: 6px; background: var(--saw-card); box-shadow: inset 0 0 0 1px var(--saw-line); font-size: 13px; font-weight: 700; }
.cys-name { display: grid; flex: 1; min-width: 0; line-height: 1.3; }
.cys-name b { overflow: hidden; font-size: 12.5px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.cys-name small { color: var(--saw-muted); font-size: 11px; }
.cys-radios .cys-name b { white-space: normal; }
/* The search finds more dollars than fit; the list fades where it keeps scrolling. */
.cys-more { -webkit-mask-image: linear-gradient(#000 78%, transparent); mask-image: linear-gradient(#000 78%, transparent); }
.cys-rows { overflow: hidden; }
.cys-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; min-height: 38px; padding: 5px 11px; font-size: 12px; }
.cys-row + .cys-row { border-top: 1px solid var(--saw-line); }
.cys-row b { overflow: hidden; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.cys-row.is-new { animation: cys-flash 1.3s ease 0.2s both; }
@keyframes cys-flash { 0% { background: transparent; } 30% { background: var(--saw-blue-fill); } 100% { background: transparent; } }
.cys-flat { padding: 4px 0; }

.cys-inv { display: grid; gap: 6px; padding: 12px; }
.cys-inv-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; margin-bottom: 4px; }
.cys-inv-head > span { display: grid; line-height: 1.3; }
.cys-inv-head b { font-size: 14px; font-weight: 600; }
.cys-inv-head small { color: var(--saw-muted); font-size: 11.5px; }
.cys-line { display: grid; grid-template-columns: minmax(0, 1fr) 2em 6.5em; gap: 6px; border-bottom: 1px solid var(--saw-line); padding: 6px 0; font-size: 12px; }
.cys-line span:first-child { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cys-tot { display: flex; justify-content: space-between; gap: 8px; font-size: 12px; }
.cys-tax { color: var(--saw-muted); }
.cys-dates { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-bottom: 4px; }
.cys-due { border-radius: 6px; background: var(--saw-wash); padding: 7px 9px; font-weight: 600; }
.cys-total { border-top: 1px solid var(--saw-line); padding-top: 6px; font-size: 13.5px; font-weight: 700; }

.cys-field { display: grid; gap: 2px; border: 1px solid var(--saw-line); border-radius: 6px; padding: 5px 9px; font-size: 12px; }
.cys-field small { color: var(--saw-muted); font-size: 10px; }
.cys-comp { display: grid; grid-template-columns: minmax(0, 1fr) 5.5em; gap: 6px; }
.saw-layer.is-on .cys-comp { animation: cys-in 0.4s cubic-bezier(0.33, 1, 0.68, 1) calc(0.2s + var(--i) * 0.15s) both; }
.cys-rate span { font-variant-numeric: tabular-nums; }
.cys-add { justify-self: start; padding: 3px 9px; font-size: 11.5px; }
.cys-switch { position: relative; flex: none; width: 28px; height: 16px; border-radius: 8px; background: var(--saw-line); }
.cys-switch::after { content: ""; position: absolute; top: 2px; left: 2px; width: 12px; height: 12px; border-radius: 50%; background: #fff; }

.cys-radios li { gap: 9px; }
.cys-radio { flex: none; width: 16px; height: 16px; border: 2px solid var(--saw-muted); border-radius: 50%; }
.cys-radio.is-on { border-color: var(--saw-ink); box-shadow: inset 0 0 0 3px var(--saw-card), inset 0 0 0 8px var(--saw-ink); }

@media (prefers-reduced-motion: reduce) {
  .saw-layer.is-on .cys-list li, .saw-layer.is-on .cys-comp, .cys-row.is-new { animation: none; }
}
</style>
